/** QA/QC overview dashboard tab ids (Oil CCE | Gas CCE | DL | Separator). */

export const QAQC_OVERVIEW_TAB_OIL_CCE = "oilCce";
export const QAQC_OVERVIEW_TAB_GAS_CCE = "gasCce";
export const QAQC_OVERVIEW_TAB_DL = "dlTests";
export const QAQC_OVERVIEW_TAB_SEPARATOR = "separatorTests";

const VALID_TABS = new Set([
  QAQC_OVERVIEW_TAB_OIL_CCE,
  QAQC_OVERVIEW_TAB_GAS_CCE,
  QAQC_OVERVIEW_TAB_DL,
  QAQC_OVERVIEW_TAB_SEPARATOR,
]);

export function isDlLikeOverviewTab(tab) {
  return tab === QAQC_OVERVIEW_TAB_DL || tab === QAQC_OVERVIEW_TAB_SEPARATOR;
}

export function isValidQaqcOverviewTab(tab) {
  return VALID_TABS.has(tab);
}

export function parseQaqcOverviewTabFromQuery(query) {
  if (!query || typeof query !== "object") return null;
  const raw = query.qaqcOverviewTab;
  const tab = Array.isArray(raw) ? raw[0] : raw;
  return isValidQaqcOverviewTab(tab) ? tab : null;
}

export function overviewTabFromFluidTypeQuery(query) {
  if (!query || typeof query !== "object") return QAQC_OVERVIEW_TAB_OIL_CCE;
  const raw = query.fluidType;
  const s = Array.isArray(raw) ? raw[0] : raw;
  const inner = String(s ?? "")
    .replace(/^["']+|["']+$/g, "")
    .toLowerCase();
  if (inner === "gas" || inner.includes("gas")) {
    return QAQC_OVERVIEW_TAB_GAS_CCE;
  }
  return QAQC_OVERVIEW_TAB_OIL_CCE;
}

export function overviewTabFromTestRoute(route) {
  const fromQuery = parseQaqcOverviewTabFromQuery(route?.query);
  if (fromQuery) return fromQuery;

  const testType = String(route?.params?.testType ?? "").toLowerCase();
  if (testType.includes("separator")) return QAQC_OVERVIEW_TAB_SEPARATOR;
  if (testType.includes("dl")) return QAQC_OVERVIEW_TAB_DL;
  if (
    testType.includes("cce") ||
    testType.includes("cvd") ||
    testType.includes("composition")
  ) {
    return overviewTabFromFluidTypeQuery(route?.query);
  }
  return QAQC_OVERVIEW_TAB_OIL_CCE;
}

export function overviewTabFromNavigationTarget(navigationTarget, listQuery = {}) {
  if (navigationTarget?.testKind === "separator") return QAQC_OVERVIEW_TAB_SEPARATOR;
  if (navigationTarget?.testKind === "dl") return QAQC_OVERVIEW_TAB_DL;
  return overviewTabFromFluidTypeQuery(listQuery);
}

export function overviewTabFromDatasetLabel(datasetLabel) {
  const label = String(datasetLabel ?? "").toLowerCase();
  if (label.includes("separator")) return QAQC_OVERVIEW_TAB_SEPARATOR;
  if (label.includes("dl")) return QAQC_OVERVIEW_TAB_DL;
  if (label.includes("gas")) return QAQC_OVERVIEW_TAB_GAS_CCE;
  return QAQC_OVERVIEW_TAB_OIL_CCE;
}

export function overviewTabFromDistributionNavContext(ctx) {
  if (ctx === "separator") return QAQC_OVERVIEW_TAB_SEPARATOR;
  if (ctx === "dl") return QAQC_OVERVIEW_TAB_DL;
  if (ctx === "gas") return QAQC_OVERVIEW_TAB_GAS_CCE;
  return QAQC_OVERVIEW_TAB_OIL_CCE;
}

/** `public/api/samples/qc/distribution-plot` body `testName` from chip nav context. */
export function qcDistributionTestNameForChipContext(ctx) {
  const c = String(ctx || "").toLowerCase();
  if (c === "separator") return "separatortests";
  if (c === "dl") return "dltests";
  return "ccetests";
}

export function distributionChipLabelForContext(ctx) {
  const c = String(ctx || "").toLowerCase();
  if (c === "separator") return "Separator";
  if (c === "dl") return "DL";
  return "CCE";
}

/** Avoid matching generic/CCE feed-deviation plots on the Separator tab. */
export function feedDeviationPlotNameCandidatesForTab(tab) {
  if (tab === QAQC_OVERVIEW_TAB_SEPARATOR) {
    return ["OverallFeedDeviationSep"];
  }
  return ["OverallFeedDeviationDL", "OverallFeedDeviation", "FeedDeviationMolePercent"];
}

export function mergeOverviewTabIntoQuery(query, overviewTab) {
  const out = { ...(query || {}) };
  if (isValidQaqcOverviewTab(overviewTab)) {
    out.qaqcOverviewTab = overviewTab;
  }
  return out;
}

export function buildQaqcUnifiedTestLocation({
  tenantPrefix,
  sampleId,
  testType,
  testId,
  well,
  fluidSampleID,
}) {
  const q = {
    sampleId: String(sampleId),
    qaqcCtx: well ? "well" : "direct",
  };
  if (well) {
    q.well = well;
  }
  if (fluidSampleID != null && String(fluidSampleID).trim() !== "") {
    q.fluidSampleID = String(fluidSampleID);
  }
  return {
    name: "UnifiedTestView",
    params: {
      tenantPrefix,
      sampleId: String(sampleId),
      testType,
      testId: String(testId),
    },
    query: mergeOverviewTabIntoQuery(
      q,
      overviewTabFromTestRoute({ params: { testType }, query: q })
    ),
  };
}

function encodeAssetPathSeg(value) {
  return value
    ? encodeURIComponent(value).replace(/'/g, "%27").replace(/ /g, "-")
    : "NotAvail";
}

export function buildExperimentalTestPath({
  tenantPrefix,
  asset,
  field,
  well,
  sampleId,
  testType,
  testId,
}) {
  const encodedAsset = encodeAssetPathSeg(asset);
  const encodedField = encodeAssetPathSeg(field);
  const encodedWell = encodeAssetPathSeg(well);
  const encodedSample = sampleId
    ? encodeURIComponent(sampleId).replace(/'/g, "%27").replace(/ /g, "-")
    : "";
  const base = `/${tenantPrefix}/assets/${encodedAsset}/${encodedField}/${encodedWell}`;
  if (encodedSample) {
    return `${base}/${encodedSample}/${testType}/${testId}`;
  }
  return `${base}/${testType}/${testId}`;
}
